<template>
  <sf-modal-portal
    modal-id="mark-proposal-as-finalized-modal"
    :scrollable-content="true"
    size="s"
    @opened="onModalOpened"
  >
    <template #title>{{
      I18n.t('reconciliation_proposal.pulse.mark_as_finalized')
    }}</template>
    <template #messages>
      <sf-modal-message v-dompurify-html="hint.text" :variant="hint.type" />
      <sf-modal-message v-if="failed" variant="error">{{
        I18n.t('reconciliation_proposal.pulse.failed_to_mark_as_finalized')
      }}</sf-modal-message>
    </template>

    <template #body>
      <form class="sf-form sf-form-full sf-below-3">
        <sf-input
          v-model="message"
          data-testid="message-field"
          component-type="textarea"
          :label="
            I18n.t(
              'reconciliation_proposal.pulse.mark_as_finalized_message_label'
            )
          "
          :placeholder="
            I18n.t(
              'reconciliation_proposal.pulse.mark_as_finalized_message_placeholder'
            )
          "
        />
      </form>
    </template>
    <template #footer>
      <sf-button variant="default" @click="close">
        {{ I18n.t('confirmation.cancel') }}
      </sf-button>
      <sf-button
        :disabled="submitting"
        variant="success"
        data-testid="finalize-proposal"
        @click="save"
      >
        {{ submitButtonText }}
      </sf-button>
    </template>
  </sf-modal-portal>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { SfInput } from '@app/shared/vue/components/sf-input'
import SFetch from '@app/utils/sfetch'
import {
  SfModalPortal,
  SfModalMessage,
} from '@app/shared/vue/components/sf-modal'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import { usePulseWorkflowProgress } from '@app/application/workflow-overview-navigation/store/pulse-workflow-progress'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { messageTypes } from '@app/shared/vue/components/sf-modal/constants'

const I18n = useI18n()

interface Props {
  item: string
  endpoint: string
  redirectPath?: string
  finalizeWorkflow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  finalizeWorkflow: false,
  redirectPath: undefined,
})

const { workflowProgress } = storeToRefs(usePulseWorkflowProgress())
const workflowProgressSnapshot = ref(workflowProgress.value)
const message = ref('')
const failed = ref(false)
const submitting = ref(false)

const completed = computed(
  () => workflowProgressSnapshot.value === 100 || !props.finalizeWorkflow
)

// eslint-disable-next-line no-undef
defineOptions({
  name: 'MarkProposalAsFinalizedModal',
})

const hint = computed(() => {
  const data = completed.value
    ? {
        key: 'reconciliation_proposal.pulse.mark_as_finalized_hint.done',
        type: messageTypes.info,
      }
    : {
        key: 'reconciliation_proposal.pulse.mark_as_finalized_hint.ongoing_html',
        type: messageTypes.warning,
      }

  return {
    text: I18n.t(data.key, { item: props.item }),
    type: data.type,
  }
})

const submitButtonText = computed(() => {
  return I18n.t(
    completed.value
      ? 'reconciliation_proposal.pulse.mark_as_finalized'
      : 'reconciliation_proposal.pulse.mark_as_finalized_with_missing_fields'
  )
})

const save = async () => {
  submitting.value = true
  try {
    await SFetch.json(props.endpoint, {
      method: 'PUT',
      body: {
        message: message.value,
        finalize_workflow: props.finalizeWorkflow,
      },
    })
    window.Turbolinks.visit(props.redirectPath || window.location.href)
  } catch {
    failed.value = true
  } finally {
    submitting.value = false
  }
}

const reset = () => {
  failed.value = false
}

const close = () => {
  window.modalCitizen.closeModalPortal()
  reset()
}
// workflow progress might be updated while the modal is open, but we want to keep the modal UI unchanged while open
function onModalOpened() {
  workflowProgressSnapshot.value = workflowProgress.value
}
</script>
